import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth'

/**
 * Snooze-all and mute-this-event, in one place.
 *
 * Both actions existed twice — once in `NotificationsPage`, once in
 * `notification-bell` — from the same two tables with the same two upserts, and
 * the copies had already drifted in the way duplicated code always drifts: the
 * bell checked `error` and raised a toast either way, the page checked nothing
 * and said nothing. Same menu item, same row component, two different products:
 * from the bell «Snooze 1 time» confirmed itself, from /notifications it looked
 * like a dead control whether it had worked or not.
 *
 * One implementation, and it is the one with the receipt.
 */
export function useNotificationActions() {
  const { t } = useTranslation()
  /* The id, not the whole user object: `useCallback`'s stated dependency has to
     be the thing the body reads, or the React Compiler refuses to keep the
     memoization (it infers `user`, the source says `user?.id`, and the two do
     not match). */
  const userId = useAuthStore((s) => s.user?.id)

  const snoozeForHours = useCallback(
    async (hours: number) => {
      if (!userId) return
      const { error } = await supabase.from('notification_preferences').upsert(
        {
          user_id: userId,
          snoozed_until: new Date(Date.now() + hours * 60 * 60 * 1000).toISOString(),
        },
        { onConflict: 'user_id' }
      )
      if (error) {
        toast.error(t('common.error'))
        return
      }
      toast.success(t('notifications.snoozed', { count: hours }))
    },
    [userId, t]
  )

  const muteEvent = useCallback(
    async (eventId: string) => {
      if (!userId) return
      const { error } = await supabase.from('event_notification_overrides').upsert(
        {
          user_id: userId,
          event_id: eventId,
          muted: true,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,event_id' }
      )
      if (error) {
        toast.error(t('common.error'))
        return
      }
      toast.success(
        t('notifications.eventMuted', { defaultValue: 'Varsler for arrangementet dempet' })
      )
    },
    [userId, t]
  )

  return { snoozeForHours, muteEvent }
}
